//Imports
import React, { useMemo, useState, useEffect } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
    Button,
    Form,
    Modal
} from "@gpstrackit/gtc-react-controls";

//Components
import { NotificationContainer } from "Components/Notifications";
import Tabs, { TabContainer } from 'Components/Tabs';

//Slice
import { resetWorkOrderSettings, resetWorkOrderComments, resetWorkOrderHistoryLogs } from 'Redux/actions'
import { resetWorkOrder } from "./redux/slice";

//Tabs
import Settings from './views/settings';
import Comments from './views/comments';
import HistoryLogs from './views/historyLogs';

//Variables
const INIT_DATA = {
    scheduledAt: null,
    customer: null,
    type: null,
    formName: null,
    estimatedDurationMins: null,
    severity: null,
    driver: null
};

const CreateEditWorkOrder = ({ refresh, customerId }) => {

    const { messages } = useIntl();
    const dispatch = useDispatch();

    //Form refs
    const createEditWorkOrderFormRef = Form.useForm();

    //Redux States
    const { loading, loadingWorkOrderTypes, loadingWorkOrderStatus, loadingWorkOrder } = useSelector((state) => state.workOrdersSettingsWorkOrderRedux);
    const { open, workOrderId, preloadedData } = useSelector((state) => state.workOrdersCreateEditWorkOrderRedux);
    const loadingComments = useSelector((state) => state.workOrdersCommentsWorkOrderRedux.loading);
    const loadingHistoryLogs = useSelector((state) => state.workOrdersHistoryLogsWorkOrderRedux?.loading);
    const workOrder = useSelector((state) => state.workOrdersSettingsWorkOrderRedux?.workOrder);

    //Local States
    const [openModalConfirmClose, setOpenModalConfirmClose] = useState(false);
    const [changes, setChanges] = useState(INIT_DATA);
    const [tab, setTab] = useState(0);

    //Other hooks
    const title = useMemo(() => { return workOrderId ? `${messages['workOrder_createEdit_editTitle']} ${workOrder?.workOrderNumber ? `"${workOrder?.workOrderNumber}"` : ""}` : messages['workOrder_createEdit_createTitle'] }, [workOrderId, workOrder]);

    // Initialize form with preloaded data if available
    useEffect(() => {
        if (preloadedData && open && !workOrderId) {
            
            // Crear una copia nueva del objeto preloadedData
            const preloadedDataCopy = { ...preloadedData };
            
            // Verify that estimatedDurationMins is a valid number
            if (preloadedDataCopy.estimatedDurationMins) {
                preloadedDataCopy.estimatedDurationMins = Number(preloadedDataCopy.estimatedDurationMins);
            }
            
            // Set pre-filled data in the form
            createEditWorkOrderFormRef?.setValues(preloadedDataCopy);
            
            // Mark fields that have preloaded data as changed
            const newChanges = {};
            if (preloadedDataCopy.scheduledAt) newChanges.scheduledAt = true;
            if (preloadedDataCopy.driver) newChanges.driver = true;
            if (preloadedDataCopy.estimatedDurationMins) newChanges.estimatedDurationMins = true;
            
            setChanges(prevChanges => ({
                ...newChanges,
                ...prevChanges
            }));
        }
    }, [preloadedData, open, workOrderId]);

    const handleClose = () => {
        dispatch(resetWorkOrder());
        dispatch(resetWorkOrderSettings());
        dispatch(resetWorkOrderComments());
        dispatch(resetWorkOrderHistoryLogs());
        createEditWorkOrderFormRef?.resetValues();
        setTab(0);
    };

    const validateObject = (obj) => {
        return Object.values(obj).some(value => value !== null);
    }

    const tabs = [
        {
            label: messages['workOrder_createEdit_settings'],
            icon: "event_available",
            component: <Settings
                refresh={refresh}
                createEditWorkOrderFormRef={createEditWorkOrderFormRef}
                close={handleClose}
                changes={changes}
                setChanges={setChanges}
                customerId={customerId}
            />,
        },
        {
            label: messages['workOrder_createEdit_comments'],
            icon: "question_answer",
            component: <Comments />,
        },
        {
            label: messages['workOrder_createEdit_historyLogs'],
            icon: "history",
            component: <HistoryLogs tab={tab} />,
        }
    ];

    return (
        <>
            <Modal
                title={title && title.length > 48 ? title.substring(0, 45) + '..."' : title}
                open={open}
                handleClose={handleClose}
                position="right"
                style={{ zIndex: "auto" }}
                size="small"
                loading={loading || loadingWorkOrderTypes || loadingWorkOrderStatus || loadingComments || loadingHistoryLogs || loadingWorkOrder}
                footer={
                    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <Button
                            isDisabled={loading}
                            style={{ marginRight: 8 }}
                            onClick={() => {
                                let validateChanges = validateObject(changes)
                                if (validateChanges) {
                                    setOpenModalConfirmClose(true)
                                } else {
                                    handleClose()
                                }


                            }}
                            variant={tab === 0 ? "secondary" : "primary"}
                        >
                            {tab === 0 ? messages["cancel"] : messages["close"]}
                        </Button>
                        {
                            tab === 0 && (
                                <Button
                                    isDisabled={loading || workOrderId && (!(validateObject(changes) && workOrderId))}
                                    onClick={() => {
                                        createEditWorkOrderFormRef?.submit()
                                    }}
                                    variant="primary"
                                >
                                    {messages["save"]}
                                </Button>
                            )
                        }
                    </div>
                }
            >
                <div>
                    {
                        workOrderId ? (
                            <div style={{ padding: '0 24px' }}>
                                <Tabs
                                    tab={tab}
                                    type="icon-indicator"
                                    border
                                    onChange={(value) => setTab(value)}
                                >
                                    {tabs.map((item, index) => {
                                        const { label, component, icon } = item;
                                        return (
                                            <TabContainer key={index} label={label} index={index} iconIndicator={icon} style={{ padding: '24px 0' }}>
                                                {component}
                                            </TabContainer>
                                        );
                                    })}
                                </Tabs>
                            </div>

                        ) : (
                            <Settings
                                refresh={refresh}
                                createEditWorkOrderFormRef={createEditWorkOrderFormRef}
                                close={handleClose}
                                changes={changes}
                                setChanges={setChanges}
                                customerId={customerId}
                                preloadedData={preloadedData}
                            />
                        )
                    }
                </div>
                <NotificationContainer />
            </Modal>
            <Modal
                title={messages['workOrder_createEdit_warning']}
                open={openModalConfirmClose}
                loading={loading}
                position="center"
                size="small"
                style={{ height: 200 }}
                footer={(
                    <div style={{ display: 'flex', alignItems: "center", justifyContent: 'end', width: '100%' }}>
                        <Button
                            disabled={loading}
                            style={{ marginRight: 8 }}
                            onClick={() => setOpenModalConfirmClose(false)}
                            variant='secondary'
                        >
                            {messages['back']}
                        </Button>

                        <Button
                            disabled={loading}
                            onClick={() => {
                                setOpenModalConfirmClose(false);
                                handleClose();
                            }}
                            variant='primary'
                        >
                            {messages['close']}
                        </Button>
                    </div>
                )}
            >
                <div style={{ padding: 20 }}>
                    {messages['workOrder_createEdit_warningMessage']}
                </div>
            </Modal>
        </>
    );
};

export default CreateEditWorkOrder;